/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import React from "react";
import { ApolloError, useMutation, useQuery } from "@apollo/client";
import {
  Grid,
  Typography,
  InputAdornment,
  Tooltip,
  IconButton,
  Divider,
  Paper,
  Button,
} from "@mui/material";
import { NavigateNext as NavigateNextIcon, InfoOutlined as InfoOutlinedIcon, Send as SendIcon } from "@mui/icons-material";
import { usePostgresViewer } from "components/viewer/context";
import { InputWithErrorLabel } from "components/haven-design/form/inputs";
import { GET_PLAN_BY_ID } from "pages/dashboard/admin/plans/queries";
import { useNewPropertyStyles } from "../styles";
import NextButton from "./next-button";
import { SEND_PROPERTY_QUOTE_EMAIL } from "./mutations";
import type { CommonStepProps } from "../step-props";
import type {
  SendPropertyQuoteEmail as SendPropertyQuoteEmailResult,
  SendPropertyQuoteEmailVariables,
} from "types/gql-types";

type BillingCycle = "annually" | "quarterly";

/** Org-wide per-unit (`threshold_type_id` 1): quote has no list MRR; do not infer from units. */
const THRESHOLD_TYPE_ORG_ID = 1;

type SendQuoteMutation = (
  options?: { variables: SendPropertyQuoteEmailVariables },
) => Promise<{ data?: SendPropertyQuoteEmailResult | null }>;

type PricingSnapshot = {
  minimumPrice?: number | null;
  annualDiscountPercent?: number | null;
  unitThreshold?: number | null;
  monthlyPricePerUnit?: number | null;
  thresholdTypeId?: number | string | null;
  pricingType?: { name?: string | null } | null;
} | null | undefined;

const UNIT_COUNT_MAX = 20_000;

const toFiniteOrNull = (numericValue: number): number | null =>
  (Number.isFinite(numericValue) ? numericValue : null);

const thresholdTypeIdAsNumber = (
  raw: number | string | null | undefined,
): number => {
  if (typeof raw === "number") return raw;
  if (typeof raw === "string" && raw !== "") return Number.parseInt(raw, 10);
  return NaN;
};

/**
 * List monthly before annual billing discount.
 * Property per-unit: max(units × rate, minimum), aligned with server `monthly_base_price`.
 */
const listMonthlyBeforeAnnualDiscount = (
  pricing: PricingSnapshot,
  units: number,
  quoteMonthlyBeforeDiscount: number,
): number => {
  if (!pricing) return quoteMonthlyBeforeDiscount;
  if (thresholdTypeIdAsNumber(pricing.thresholdTypeId) === THRESHOLD_TYPE_ORG_ID) {
    return quoteMonthlyBeforeDiscount;
  }

  const pricingName = (pricing.pricingType?.name || "").toLowerCase();
  const isFlat = pricingName.includes("flat");
  const isPerUnit = pricingName.includes("unit");
  const floor = Number(pricing.minimumPrice ?? 0);
  const mppu = toFiniteOrNull(Number(pricing.monthlyPricePerUnit as unknown as number));

  if (isFlat && Number.isFinite(floor)) return floor;
  if (isPerUnit && mppu !== null && units >= 0 && Number.isFinite(units)) {
    const fromUnits = Math.max(units * mppu, floor);
    return Number.isFinite(fromUnits) ? fromUnits : quoteMonthlyBeforeDiscount;
  }
  if (isPerUnit && mppu === null && Number.isFinite(floor)) return floor;
  return quoteMonthlyBeforeDiscount;
};

const formatCurrency = (
  amount: number,
  options: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {},
): string => {
  const {
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
  } = options;
  return `$${amount.toLocaleString("en-US", {
    minimumFractionDigits,
    maximumFractionDigits,
  })}`;
};

const buildPriceLabels = (opts: {
  pricing: PricingSnapshot;
  units: number;
  monthlyBeforeDiscount: number;
  priceLabel: string;
}): { left: string; right: string } => {
  const { pricing, units, monthlyBeforeDiscount, priceLabel } = opts;
  const floor = Number(pricing?.minimumPrice ?? 0);
  const pricingName = (pricing?.pricingType?.name || "").toLowerCase();
  const isFlat = pricingName.includes("flat");
  const isPerUnit = pricingName.includes("unit");
  const pricePerUnit = toFiniteOrNull(
    Number(pricing?.monthlyPricePerUnit as unknown as number),
  );

  const left = (() => {
    if (!pricing) {
      return "--";
    }
    if (isFlat) {
      return priceLabel;
    }
    if (isPerUnit && pricePerUnit !== null) {
      const productCents = Math.round(pricePerUnit * units * 100);
      const floorCents = Math.round(floor * 100);
      let line = `${formatCurrency(pricePerUnit)}/unit × ${units.toLocaleString("en-US")} units`;
      if (units > 0 && productCents < floorCents) {
        line += ` (monthly minimum ${formatCurrency(floor)})`;
      }
      return line;
    }
    if (isPerUnit) {
      return `Monthly minimum ${formatCurrency(floor)}/month`;
    }
    return priceLabel;
  })();

  const listMonthly = listMonthlyBeforeAnnualDiscount(
    pricing,
    units,
    monthlyBeforeDiscount,
  );
  const right = `${formatCurrency(listMonthly, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}/month`;

  return { left, right };
};

const RequirementsList: React.FC = () => (
  <>
    <Typography variant="subtitle1">You will need:</Typography>
    <ul>
      <li>Legal property name</li>
      <li>Property address</li>
      <li>Property phone and email address</li>
      <li>Unit count</li>
      <li>Accounting email</li>
      <li>Contract signer name, title, email</li>
    </ul>
  </>
);

const PropertyNameField: React.FC<{
  value: string;
  onChange: (v: string) => void;
}> = ({ value, onChange }) => (
  <InputWithErrorLabel
    required
    fullWidth
    name="propertyName"
    label="Property Name"
    margin="none"
    value={value}
    onChange={(e) => onChange((e.target as HTMLInputElement).value)}
    variant="outlined"
    size="medium"
  />
);

// Property email/phone are collected in Step 3 (Property Info);
// accounting email is collected in Step 5 (Payment)

const UnitCountWithPrice: React.FC<{
  unitCount: number | "";
  setUnitCount: (v: number | "") => void;
  monthlyBeforeDiscount: number;
  priceLabel: string;
  pricing: PricingSnapshot;
}> = ({ unitCount, setUnitCount, monthlyBeforeDiscount, priceLabel, pricing }) => {
  const units = typeof unitCount === "number" && Number.isFinite(unitCount) ? unitCount : 0;
  const { left: leftLabel, right: rightLabel } = buildPriceLabels({
    pricing,
    units,
    monthlyBeforeDiscount,
    priceLabel,
  });

  return (
    <>
      <InputWithErrorLabel
        required
        fullWidth
        name="unitCount"
        label="Unit Count"
        margin="none"
        type="number"
        value={unitCount}
        onChange={(e) => {
          const val = e.target.value === "" ? "" : Number(e.target.value);
          setUnitCount(val);
        }}
        variant="outlined"
        size="medium"
        slotProps={{
          htmlInput: {
            min: 1,
            max: UNIT_COUNT_MAX,
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title="We use unit count to estimate pricing.">
                <IconButton size="small" aria-label="unit count info">
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />
      <div style={{ display: "flex", alignItems: "center", marginTop: 8 }}>
        <Typography variant="body2" style={{ marginRight: 8 }}>
          {leftLabel}
        </Typography>
        <NavigateNextIcon fontSize="small" />
        <Typography variant="body2" style={{ marginLeft: 8 }}>
          {rightLabel}
        </Typography>
      </div>
    </>
  );
};

type PricingSummaryProps = {
  setupFeeOneTime: number;

  /** List monthly before annual billing discount (not effective MRR). */
  monthlyListBeforeDiscount: number;
  annualDiscountAmount: number;
  annualCost: number;
  billingCycle: BillingCycle;
  invoicedLetterCosts: {
    firstPageCost: number | null;
    additionalPagesCost: number | null;
  } | null;
}

const SummaryRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <>
    <Grid size={{ xs: 8 }}>
      <Typography variant="body2">{label}</Typography>
    </Grid>
    <Grid size={{ xs: 4 }}>
      <Typography variant="body2" align="right">{value}</Typography>
    </Grid>
  </>
);

export const PricingSummary: React.FC<PricingSummaryProps> = ({
  setupFeeOneTime,
  monthlyListBeforeDiscount,
  annualDiscountAmount,
  annualCost,
  billingCycle,
  invoicedLetterCosts,
}) => {
  const classes = useNewPropertyStyles();
  const isAnnual = billingCycle === "annually";
  const showAnnualDiscount = isAnnual && annualDiscountAmount > 0;
  const fmt = (amount: number): string =>
    formatCurrency(amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const annualCostBeforeDiscount = annualCost + annualDiscountAmount;
  const total = annualCost + setupFeeOneTime;

  return (
    <Paper variant="outlined" sx={classes.quoteBox}>
      <Grid container spacing={1}>
        {isAnnual ? (
          <>
            <SummaryRow label="Annual cost:" value={fmt(annualCostBeforeDiscount)} />
            {showAnnualDiscount && (
              <SummaryRow label="Annual payment discount:" value={`-${fmt(annualDiscountAmount)}`} />
            )}
            <SummaryRow label="Set up fee:" value={fmt(setupFeeOneTime)} />
            <SummaryRow label="Total:" value={fmt(total)} />
          </>
        ) : (
          <>
            <SummaryRow label="Set up fee:" value={fmt(setupFeeOneTime)} />
            <SummaryRow label="Monthly cost:" value={fmt(monthlyListBeforeDiscount)} />
            <SummaryRow label="Annual cost:" value={fmt(annualCost)} />
          </>
        )}
        {invoicedLetterCosts?.firstPageCost && (
          <>
            <Grid size={{ xs: 8 }}>
              <Typography variant="body2">Physical Letters (first page):</Typography>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Typography variant="body2" align="right">
                {formatCurrency(invoicedLetterCosts.firstPageCost)}/letter
              </Typography>
            </Grid>
          </>
        )}
        {invoicedLetterCosts?.additionalPagesCost && (
          <>
            <Grid size={{ xs: 8 }}>
              <Typography variant="body2">Additional letter pages:</Typography>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Typography variant="body2" align="right">
                {formatCurrency(invoicedLetterCosts.additionalPagesCost)}/page
              </Typography>
            </Grid>
          </>
        )}
        {typeof invoicedLetterCosts?.firstPageCost === "number" && (
          <Grid size={{ xs: 12 }}>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
              Subject to USPS price increases
            </Typography>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

type StepAddNewPropertyProps = Pick<
CommonStepProps,
| "formik"
| "annualCost"
| "monthlyBeforeDiscount"
| "annualPaymentDiscountAmount"
| "setupFeeOneTime"
| "onNext"
| "letterCosts"
>;

const PricingSection: React.FC<{
  unitCount: number | "";
  setUnitCount: (v: number | "") => void;
  annualCost: number;
  monthlyBeforeDiscount: number;
  annualPaymentDiscountAmount: number;
  setupFeeOneTime: number;
  planId: string;
  billingCycle: "annually" | "quarterly";
  invoicedLetterCosts: {
    firstPageCost: number | null;
    additionalPagesCost: number | null;
  } | null;
}> = ({
  unitCount,
  setUnitCount,
  annualCost,
  monthlyBeforeDiscount,
  annualPaymentDiscountAmount,
  setupFeeOneTime,
  planId,
  billingCycle,
  invoicedLetterCosts,
}) => {
  const { data } = useQuery(GET_PLAN_BY_ID, {
    variables: { id: planId },
    skip: !planId,
  });
  const pricing = data?.plan?.defaultPricing as PricingSnapshot;
  const pricingName = (pricing?.pricingType?.name || "").toLowerCase();
  const isFlat = pricingName.includes("flat");
  const isPerUnit = pricingName.includes("unit");
  const base = Number(pricing?.minimumPrice ?? 0);
  const mppu = toFiniteOrNull(Number(pricing?.monthlyPricePerUnit as unknown as number));
  const units =
    typeof unitCount === "number" && Number.isFinite(unitCount) ? unitCount : 0;
  const monthlyListBeforeDiscount = listMonthlyBeforeAnnualDiscount(
    pricing,
    units,
    monthlyBeforeDiscount,
  );
  const priceLabel = (() => {
    if (!pricing) return "--";
    if (isFlat) return `${formatCurrency(base)}/month`;
    if (isPerUnit && mppu !== null) return `${formatCurrency(mppu)}/unit/month`;
    if (isPerUnit) return `From ${formatCurrency(base)}/month minimum`;
    return "--";
  })();
  return (
    <>
      <Grid size={{ xs: 12, sm: 6 }}>
        <UnitCountWithPrice
          unitCount={unitCount}
          setUnitCount={setUnitCount}
          monthlyBeforeDiscount={monthlyBeforeDiscount}
          priceLabel={priceLabel}
          pricing={pricing}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <PricingSummary
          setupFeeOneTime={setupFeeOneTime}
          monthlyListBeforeDiscount={monthlyListBeforeDiscount}
          annualDiscountAmount={annualPaymentDiscountAmount}
          annualCost={annualCost}
          billingCycle={billingCycle}
          invoicedLetterCosts={invoicedLetterCosts}
        />
      </Grid>
    </>
  );
};

const FooterActions: React.FC<{
  onNext: () => void;
  isValid: boolean;
  onSendQuote: () => void;
  sendQuoteLoading: boolean;
  sendQuoteError: string | null;
  sendQuoteOk: boolean;
}> = ({
  onNext,
  isValid,
  onSendQuote,
  sendQuoteLoading,
  sendQuoteError,
  sendQuoteOk,
}) => (
  <>
    <Grid size={{ xs: 12 }}>
      <Divider />
    </Grid>
    <Grid size={{ xs: 12 }}>
      <Button
        variant="contained"
        color="primary"
        startIcon={<SendIcon />}
        onClick={onSendQuote}
        disabled={!isValid || sendQuoteLoading}
      >
        Send Quote via email
      </Button>
      {sendQuoteError && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {sendQuoteError}
        </Typography>
      )}
      {sendQuoteOk && !sendQuoteError && (
        <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
          Quote sent. Check your inbox.
        </Typography>
      )}
    </Grid>
    <Grid size={{ xs: 12 }}>
      <NextButton
        onNext={onNext}
        isValid={isValid}
      />
    </Grid>
  </>
);

const StepAddNewProperty: React.FC<StepAddNewPropertyProps> = (props) => {
  const classes = useNewPropertyStyles();
  const viewer = usePostgresViewer();
  const {
    formik,
    annualCost,
    monthlyBeforeDiscount,
    annualPaymentDiscountAmount,
    setupFeeOneTime,
    onNext,
  } = props;
  const invoicedLetterCosts = props.letterCosts ?? null;
  const [sendQuote, { loading: sendQuoteLoading }] = useMutation(SEND_PROPERTY_QUOTE_EMAIL);
  const [sendQuoteError, setSendQuoteError] = React.useState<string | null>(null);
  const [sendQuoteOk, setSendQuoteOk] = React.useState(false);
  const unitCountHasYupError = Boolean(formik.errors.unitCount);
  const unitCountValid =
    typeof formik.values.unitCount === "number" &&
    Number.isFinite(formik.values.unitCount) &&
    !unitCountHasYupError;
  const propertyNameFilled = (formik.values.propertyName || "").trim() !== "";
  const isValid = unitCountValid && propertyNameFilled;

  /* eslint-disable max-statements -- quote email validation + mutation */
  const handleSendQuote = async (): Promise<void> => {
    setSendQuoteError(null);
    setSendQuoteOk(false);
    await formik.setTouched({ propertyName: true, unitCount: true }, true);
    const errs = await formik.validateForm();
    if (errs.propertyName || errs.unitCount) return;
    const email = (viewer?.email || "").trim();
    if (!email) {
      setSendQuoteError("Your account does not have an email address on file.");
      return;
    }
    const uc = formik.values.unitCount;
    if (typeof uc !== "number" || !Number.isFinite(uc)) return;
    const planId = (formik.values.planId || "").trim();
    if (!planId) {
      setSendQuoteError("Please select a plan on the previous step.");
      return;
    }
    try {
      const res = await (sendQuote as SendQuoteMutation)({
        variables: {
          data: {
            propertyName: (formik.values.propertyName || "").trim(),
            unitCount: uc,
            planId,
            billingCycle: formik.values.billingCycle,
          },
        },
      });
      if (res.data?.sendPropertyQuoteEmail) {
        setSendQuoteOk(true);
      } else {
        setSendQuoteError("Could not send quote.");
      }
    } catch (e: unknown) {
      if (e instanceof ApolloError) {
        const gqlMsg = e.graphQLErrors?.[0]?.message;
        setSendQuoteError(gqlMsg || e.message || "Failed to send quote");
      } else {
        setSendQuoteError("Failed to send quote");
      }
    }
  };
  /* eslint-enable max-statements */

  return (
    <>
      <Grid size={{ xs: 12 }} sx={classes.section}>
        <RequirementsList />
      </Grid>
      <Grid size={{ xs: 12, lg: 10, xl: 8 }} container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <PropertyNameField
            value={formik.values.propertyName}
            onChange={(name) => formik.setFieldValue("propertyName", name)}
          />
        </Grid>
        <PricingSection
          unitCount={formik.values.unitCount}
          setUnitCount={(unitCount) => formik.setFieldValue("unitCount", unitCount, true)}
          annualCost={annualCost}
          monthlyBeforeDiscount={monthlyBeforeDiscount}
          annualPaymentDiscountAmount={annualPaymentDiscountAmount}
          setupFeeOneTime={setupFeeOneTime}
          planId={formik.values.planId}
          billingCycle={formik.values.billingCycle}
          invoicedLetterCosts={invoicedLetterCosts}
        />
        <FooterActions
          onNext={onNext}
          isValid={isValid}
          onSendQuote={handleSendQuote}
          sendQuoteLoading={sendQuoteLoading}
          sendQuoteError={sendQuoteError}
          sendQuoteOk={sendQuoteOk}
        />
      </Grid>
    </>
  );
};

export default StepAddNewProperty;
